/**
 * Перелік необоротних активів (рішення власника 2026-08-27): ОЗ + НМА +
 * МШП/МНМА на ОДНІЙ сторінці з фільтрами організація / вид / статус / пошук.
 * Канон Норми #29: InfiniteDocGrid (серверна пагінація + сортування), .card.
 * Подвійний клік → повноцінна сторінка-картка /assets/card/:kind/:id.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import type { AssetKind } from '@/api/assets';
import {
  ASSET_KIND_UA, ASSET_STATUS_UA, assetsRegistry, type AssetRegistryItem,
} from '@/api/assetsRegistry';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';

const fmt2 = (v: string | number | null | undefined) => v == null ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const selStyle = { width: 170, flex: '0 0 auto' } as const;

export default function AssetsRegistryPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const orgNames = useOrgNames();
  const f = useDocListFilters();
  const [kind, setKind] = useState<AssetKind | ''>((sp.get('kind') as AssetKind) || '');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const { datasource, total } = useInfiniteGridDatasource<AssetRegistryItem>(
    (limit, offset, sort) => assetsRegistry.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      kind: kind || undefined,
      status: status || undefined,
      q: q || undefined,
    }),
    [f.orgId, kind, status, q],
  );

  const cols = useMemo<ColDef<AssetRegistryItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: AssetRegistryItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'inventory_number', headerName: 'Інв. №', width: 110, sortable: true },
    { field: 'name', headerName: 'Назва', flex: 1, minWidth: 220, sortable: true },
    {
      field: 'kind', headerName: 'Вид', width: 95, sortable: true,
      valueFormatter: (p) => ASSET_KIND_UA[p.value as AssetKind] ?? p.value,
    },
    agOrgCol(orgNames),
    { field: 'group_name', headerName: 'Група', width: 190, sortable: true },
    { field: 'account_code', headerName: 'Рахунок', width: 90, sortable: false },
    {
      field: 'primary_cost', headerName: 'Первісна', width: 130, sortable: true,
      cellStyle: { textAlign: 'right' }, valueFormatter: (p) => fmt2(p.value),
    },
    {
      field: 'accumulated_depreciation', headerName: 'Знос', width: 130, sortable: true,
      cellStyle: { textAlign: 'right' }, valueFormatter: (p) => fmt2(p.value),
    },
    {
      field: 'residual_value', headerName: 'Залишкова', width: 130, sortable: true,
      cellStyle: { textAlign: 'right' }, valueFormatter: (p) => fmt2(p.value),
    },
    { field: 'useful_life_months', headerName: 'Строк, міс', width: 100, sortable: true },
    {
      field: 'commissioning_date', headerName: 'Введено', width: 110, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      field: 'status', headerName: 'Статус', width: 150, sortable: true,
      valueFormatter: (p) => ASSET_STATUS_UA[p.value] ?? p.value,
    },
    { field: 'department_name', headerName: 'Підрозділ', width: 150, sortable: true },
    { field: 'responsible_name', headerName: 'МВО', width: 170, sortable: true },
  ], [orgNames]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Перелік необоротних активів<TitleTotal total={total} /></h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <button className="primary" onClick={() => navigate('/assets/card/FIXED_ASSET/new')}>+ ОЗ</button>
          <button className="primary" onClick={() => navigate('/assets/card/INTANGIBLE/new')}>+ НМА</button>
          <button className="primary" onClick={() => navigate('/assets/card/LOW_VALUE/new')}>+ МШП/МНМА</button>
        </div>
      </div>
      <DocListFiltersRow>
        <select value={f.orgId} onChange={(e) => f.setOrgId(e.target.value)} style={selStyle}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value as AssetKind | '')} style={selStyle}>
          <option value="">Усі види</option>
          <option value="FIXED_ASSET">Основні засоби (ОЗ)</option>
          <option value="INTANGIBLE">Нематеріальні активи (НМА)</option>
          <option value="LOW_VALUE">МШП / МНМА</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={selStyle}>
          <option value="">Усі статуси</option>
          {Object.entries(ASSET_STATUS_UA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input placeholder="пошук: назва / інв. №" value={q}
          onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} />
      </DocListFiltersRow>
      <InfiniteDocGrid<AssetRegistryItem>
        datasource={datasource}
        columnDefs={cols}
        storageKey="assets-registry"
        onRowDoubleClicked={(r) => navigate(`/assets/card/${r.kind}/${r.id}`)}
        getRowStyle={(p) => (p.data?.status === 'WRITTEN_OFF' || p.data?.status === 'SOLD')
          ? { opacity: 0.55 } : undefined}
      />
    </div>
  );
}
